import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Download as DownloadIcon,
  CloudDownload as CloudDownloadIcon,
  Visibility as VisibilityIcon,
  Assessment as AssessmentIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  Timeline as TimelineIcon,
  Event as EventIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.paceestate.com';

const exportTypes = [
  {
    id: 'sales',
    title: 'Sales Data',
    description: 'Export all estate sales information',
    icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
    color: '#1976d2',
    endpoint: '/api/export/sales',
    defaultFields: ['id', 'title', 'status', 'start_date', 'end_date', 'address', 'city', 'state', 'total_revenue', 'commission_amount', 'created_at']
  },
  {
    id: 'inventory',
    title: 'Inventory',
    description: 'Export inventory items and their details',
    icon: <InventoryIcon sx={{ fontSize: 40 }} />,
    color: '#388e3c',
    endpoint: '/api/export/inventory',
    defaultFields: ['id', 'name', 'category', 'price', 'cost', 'quantity', 'status', 'sale_title', 'created_at']
  },
  {
    id: 'leads',
    title: 'Leads',
    description: 'Export leads and contact information',
    icon: <PeopleIcon sx={{ fontSize: 40 }} />,
    color: '#f57c00',
    endpoint: '/api/export/leads',
    defaultFields: ['id', 'first_name', 'last_name', 'email', 'phone', 'status', 'source', 'estimated_value', 'assigned_to', 'created_at']
  },
  {
    id: 'invoices',
    title: 'Invoices',
    description: 'Export invoice records',
    icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
    color: '#7b1fa2',
    endpoint: '/api/export/invoices',
    defaultFields: ['id', 'invoice_number', 'client_name', 'sale_title', 'status', 'total', 'amount_paid', 'balance_due', 'issued_date', 'due_date']
  },
  {
    id: 'commissions',
    title: 'Commissions',
    description: 'Export commission data',
    icon: <MoneyIcon sx={{ fontSize: 40 }} />,
    color: '#0097a7',
    endpoint: '/api/export/commissions',
    defaultFields: ['id', 'sale_title', 'employee_name', 'amount', 'percentage', 'status', 'payment_date', 'created_at']
  },
  {
    id: 'activity',
    title: 'Activity Logs',
    description: 'Export system activity logs (Admin only)',
    icon: <TimelineIcon sx={{ fontSize: 40 }} />,
    color: '#d32f2f',
    endpoint: '/api/export/activity',
    defaultFields: ['id', 'user_email', 'action', 'entity_type', 'description', 'created_at']
  },
  {
    id: 'appointments',
    title: 'Appointments',
    description: 'Export appointment schedules',
    icon: <EventIcon sx={{ fontSize: 40 }} />,
    color: '#5e35b1',
    endpoint: '/api/export/appointments',
    defaultFields: ['id', 'client_name', 'type', 'status', 'scheduled_at', 'duration_minutes', 'location', 'created_at']
  },
  {
    id: 'payments',
    title: 'Payments',
    description: 'Export payment transactions',
    icon: <PaymentIcon sx={{ fontSize: 40 }} />,
    color: '#00796b',
    endpoint: '/api/export/payments',
    defaultFields: ['id', 'invoice_number', 'amount', 'payment_method', 'status', 'transaction_id', 'payment_date', 'created_at']
  }
];

function DataExport() {
  const [selectedType, setSelectedType] = useState(null);
  const [exportFormat, setExportFormat] = useState('csv');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedFields, setSelectedFields] = useState([]);
  const [preview, setPreview] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setSelectedFields(type.defaultFields);
    setShowPreview(false);
    setError('');
    setSuccess('');
  };

  const handleFieldToggle = (field) => {
    if (selectedFields.includes(field)) {
      setSelectedFields(selectedFields.filter(f => f !== field));
    } else {
      setSelectedFields([...selectedFields, field]);
    }
  };

  const handlePreview = async () => {
    if (!selectedType) return;

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const params = {
        format: 'json',
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate })
      };

      selectedFields.forEach(field => {
        params[`fields[]`] = field;
      });

      const response = await axios.get(
        `${API_BASE_URL}${selectedType.endpoint}`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'json'
        }
      );

      // For JSON format, we get the data directly
      // For CSV/Excel, we need to parse differently
      let data = response.data;
      if (Array.isArray(data)) {
        setPreview(data.slice(0, 5)); // Show first 5 rows
        setShowPreview(true);
        setSuccess(`Preview showing ${Math.min(5, data.length)} of ${data.length} rows`);
      }
    } catch (err) {
      console.error('Preview error:', err);
      setError(err.response?.data?.error || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!selectedType || selectedFields.length === 0) {
      setError('Please select at least one field to export');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        format: exportFormat,
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate })
      });

      // Add fields as array parameters
      selectedFields.forEach(field => {
        params.append('fields[]', field);
      });

      const response = await axios.get(
        `${API_BASE_URL}${selectedType.endpoint}?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${selectedType.id}_export_${format(new Date(), 'yyyyMMdd_HHmmss')}.${exportFormat}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess(`Successfully exported ${selectedType.title} as ${exportFormat.toUpperCase()}`);
    } catch (err) {
      console.error('Export error:', err);
      setError(err.response?.data?.error || 'Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  const renderPreviewTable = () => {
    if (!showPreview || preview.length === 0) return null;

    return (
      <Paper sx={{ mt: 3, mb: 2 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Preview</Typography>
          <Typography variant="body2" color="text.secondary">
            Showing first {preview.length} rows
          </Typography>
        </Box>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {selectedFields.map((field) => (
                  <TableCell key={field}>
                    <Typography variant="caption" fontWeight="bold">
                      {field.replace(/_/g, ' ').toUpperCase()}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.map((row, idx) => (
                <TableRow key={idx}>
                  {selectedFields.map((field) => (
                    <TableCell key={field}>
                      {row[field] !== null && row[field] !== undefined ? String(row[field]) : '-'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Data Export Center
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Export your data for accounting, backup, or migration purposes
        </Typography>
      </Box>

      {!selectedType ? (
        <Grid container spacing={3}>
          {exportTypes.map((type) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={type.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => handleTypeSelect(type)}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                  <Box sx={{ color: type.color, mb: 2 }}>
                    {type.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {type.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {type.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button
                    size="small"
                    startIcon={<CloudDownloadIcon />}
                    sx={{ color: type.color }}
                  >
                    Select
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ color: selectedType.color, mr: 2 }}>
                {selectedType.icon}
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h5">{selectedType.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedType.description}
                </Typography>
              </Box>
              <Button onClick={() => setSelectedType(null)}>
                Back to List
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Export Format</InputLabel>
                  <Select
                    value={exportFormat}
                    label="Export Format"
                    onChange={(e) => setExportFormat(e.target.value)}
                  >
                    <MenuItem value="csv">CSV</MenuItem>
                    <MenuItem value="xlsx">Excel (XLSX)</MenuItem>
                    <MenuItem value="json">JSON</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Select Fields to Export
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose which columns to include in your export
                </Typography>
                <FormGroup>
                  <Grid container>
                    {selectedType.defaultFields.map((field) => (
                      <Grid item xs={12} sm={6} md={4} key={field}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedFields.includes(field)}
                              onChange={() => handleFieldToggle(field)}
                            />
                          }
                          label={field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={handlePreview}
                    disabled={loading || selectedFields.length === 0}
                  >
                    Preview Data
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
                    onClick={handleExport}
                    disabled={loading || selectedFields.length === 0}
                  >
                    {loading ? 'Exporting...' : `Export as ${exportFormat.toUpperCase()}`}
                  </Button>
                  <Box sx={{ flexGrow: 1 }} />
                  <Chip
                    label={`${selectedFields.length} fields selected`}
                    color={selectedFields.length > 0 ? 'primary' : 'default'}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {renderPreviewTable()}
        </Box>
      )}
    </Container>
  );
}

export default DataExport;
